// @djsem/shared — Magic Sorting / Set Flow ordering (Prompt 07).
//
// Explainable, DETERMINISTIC set-flow suggestions: a greedy pairwise ranker that proposes
// an order and always returns REASONS + WARNINGS so the DJ decides. No auto-mixing, no
// black-box ML, no external calls. Prep/Set-Builder side only — never live runtime.
//
// Honesty rules: missing BPM/key never crashes and is never invented; large BPM jumps and
// missing keys produce warnings; unapproved tracks stay in the result but are warned.

import {
  vArray, vNumber, vBoolean, vObject, vString, vUnion, type Validator,
  type ValidationResult, type DJSEMValidationResult, toDJSEMValidationResult,
} from "./validation";

// ---- contracts --------------------------------------------------------------

/** The energy/story goal a set-flow sort should optimize toward. */
export type DJSEMSetSortGoal =
  | "warmup_to_peak" | "peak_to_recovery" | "wave_energy"
  | "constant_pressure" | "semantic_story" | "manual_assist";

/** All valid {@link DJSEMSetSortGoal} values, in canonical order. */
export const SET_SORT_GOALS: DJSEMSetSortGoal[] = [
  "warmup_to_peak", "peak_to_recovery", "wave_energy",
  "constant_pressure", "semantic_story", "manual_assist",
];

/** A DJ-specified position/adjacency constraint that overrides the automatic ordering. */
export interface DJSEMManualTrackLock {
  preparationTrackId: string;
  lockedPosition?: number;
  lockedBeforeTrackId?: string;
  lockedAfterTrackId?: string;
  reason?: string;
}

/** A track's sortable attributes, as consumed by {@link SetFlowSortingService}. */
export interface DJSEMTrackSortInput {
  preparationTrackId: string;
  title: string;
  artist?: string;
  bpm?: number;
  key?: string;
  energyScore?: number;
  energyLabel?: string;
  moodTags: string[];
  semanticDomains: string[];
  semanticCategories: string[];
  approvedForLive: boolean;
  warnings: string[];
  cueIntents: string[];
}

/** Options controlling how {@link SetFlowSortingService.sortTracks} weighs and orders tracks. */
export interface DJSEMTrackSortOptions {
  goal: DJSEMSetSortGoal;
  bpmWeight: number;
  keyWeight: number;
  energyWeight: number;
  semanticWeight: number;
  approvalWeight: number;
  respectManualLocks: boolean;
  manualLocks: DJSEMManualTrackLock[];
}

/** Machine-readable code identifying why a transition scored the way it did. */
export type DJSEMTrackSortReasonCode =
  | "bpm_close" | "key_compatible" | "energy_progression" | "semantic_continuity"
  | "mood_continuity" | "approved_for_live" | "manual_lock" | "warning";

/** A single explainable contribution to a transition's score. */
export interface DJSEMTrackSortReason {
  code: DJSEMTrackSortReasonCode;
  message: string;
  scoreImpact: number;
}

/** One track's placement, score, and explanation within a sorted set. */
export interface DJSEMTrackSortResultItem {
  preparationTrackId: string;
  position: number;
  score: number;
  reasons: DJSEMTrackSortReason[];
  warnings: string[];
}

/** The full result of {@link SetFlowSortingService.sortTracks}. */
export interface DJSEMTrackSortResult {
  goal: DJSEMSetSortGoal;
  items: DJSEMTrackSortResultItem[];
  overallScore: number;
  warnings: string[];
}

/** Score, reasons, and warnings for a single track-to-track transition. */
export interface DJSEMTransitionScore {
  score: number;
  reasons: DJSEMTrackSortReason[];
  warnings: string[];
}

// ---- key compatibility (simple Camelot rules; honest about missing data) ----

// Parse "8A"/"11B"/"5a" → { n, letter }. Returns null for unknown/absent.
function parseCamelot(key?: string): { n: number; letter: "A" | "B" } | null {
  if (!key) return null;
  const m = /^(\d{1,2})\s*([abAB])$/.exec(key.trim());
  if (!m) return null;
  const n = Number(m[1]);
  if (n < 1 || n > 12) return null;
  return { n, letter: m[2].toUpperCase() as "A" | "B" };
}

/**
 * Score harmonic compatibility between two Camelot-notation keys (e.g. "8A", "11B").
 * 1.0 identical, 0.8 harmonic neighbour/relative, 0.7 same number different letter, else 0.3.
 * @param a - First key, in Camelot notation.
 * @param b - Second key, in Camelot notation.
 * @returns The compatibility score, or null if either key is missing/unparseable (caller warns, never invents).
 */
export function camelotCompatibility(a?: string, b?: string): number | null {
  const ca = parseCamelot(a); const cb = parseCamelot(b);
  if (!ca || !cb) return null; // unknown — caller warns, does not invent
  if (ca.n === cb.n && ca.letter === cb.letter) return 1;
  const adj = (x: number, y: number) => x === y || x === (y % 12) + 1 || y === (x % 12) + 1;
  if (ca.letter === cb.letter && adj(ca.n, cb.n)) return 0.8; // ±1 same mode
  if (ca.n === cb.n && ca.letter !== cb.letter) return 0.7;   // relative major/minor
  return 0.3;
}

// ---- validator --------------------------------------------------------------

const vWeight: Validator = vNumber({ min: 0, max: 100 });
/** Runtime validator for {@link DJSEMTrackSortOptions}. */
export const vTrackSortOptions: Validator<DJSEMTrackSortOptions> = vObject(
  {
    goal: vUnion(...SET_SORT_GOALS.map((g) => vString({ enum: [g] }))),
    bpmWeight: vWeight, keyWeight: vWeight, energyWeight: vWeight,
    semanticWeight: vWeight, approvalWeight: vWeight,
    respectManualLocks: vBoolean,
    manualLocks: vArray(vObject({ preparationTrackId: vString({ min: 1 }) },
      { lockedPosition: vNumber({ min: 0 }), lockedBeforeTrackId: vString({ min: 1 }), lockedAfterTrackId: vString({ min: 1 }), reason: vString({}) })),
  },
) as Validator<DJSEMTrackSortOptions>;

// ---- service (class-based; hard rule 12) ------------------------------------

const clampUnit = (n: number) => Math.max(0, Math.min(1, n));

/**
 * Explainable, deterministic Magic Sorting / Set Flow ordering. A greedy pairwise
 * ranker that proposes a track order and always returns reasons + warnings so the DJ
 * decides — no auto-mixing, no black-box ML.
 */
export class SetFlowSortingService {
  /**
   * Validate a set of sort options.
   * @param options - The candidate options to validate.
   * @returns The validation result.
   */
  validateOptions(options: unknown): DJSEMValidationResult {
    const base: ValidationResult = vTrackSortOptions(options);
    const issues = [...base.issues];
    const o = options as DJSEMTrackSortOptions;
    for (const w of ["bpmWeight", "keyWeight", "energyWeight", "semanticWeight", "approvalWeight"] as const) {
      const v = o?.[w];
      if (typeof v === "number" && (!Number.isFinite(v) || v < 0)) issues.push({ path: `$.${w}`, message: `${w} must be a finite value >= 0` });
    }
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "sort_options");
  }

  /**
   * Score a single pairwise transition with explainable reasons and warnings.
   * @param from - The preceding track, or null if `to` is the seed (first) track.
   * @param to - The track being transitioned into.
   * @param options - Weights and goal controlling how the score is computed.
   * @returns The transition score, contributing reasons, and warnings.
   */
  scoreTransition(from: DJSEMTrackSortInput | null, to: DJSEMTrackSortInput, options: DJSEMTrackSortOptions): DJSEMTransitionScore {
    const reasons: DJSEMTrackSortReason[] = [];
    const warnings: string[] = [];
    let score = 0;

    // Approval (independent of `from`)
    if (to.approvedForLive) {
      const impact = 0.1 * options.approvalWeight;
      score += impact;
      reasons.push({ code: "approved_for_live", message: `${to.title} is approved for live.`, scoreImpact: impact });
    } else {
      warnings.push(`${to.title} is not approved for live.`);
      if (options.approvalWeight > 1) score -= 0.05 * options.approvalWeight;
    }
    for (const w of to.warnings) warnings.push(`${to.title}: ${w}`);

    if (from === null) return { score, reasons, warnings }; // seed

    // BPM proximity
    if (typeof from.bpm === "number" && typeof to.bpm === "number") {
      const diff = Math.abs(from.bpm - to.bpm);
      const pct = from.bpm > 0 ? diff / from.bpm : 1;
      const prox = clampUnit(1 - pct / 0.16); // 16% jump → 0
      const impact = prox * options.bpmWeight;
      score += impact;
      if (prox > 0.5) reasons.push({ code: "bpm_close", message: `BPM ${from.bpm}→${to.bpm} (Δ${diff.toFixed(0)}) is close.`, scoreImpact: impact });
      if (diff > 16 || pct > 0.16) warnings.push(`Large BPM jump ${from.bpm}→${to.bpm} (Δ${diff.toFixed(0)}).`);
    } else {
      warnings.push(`Missing BPM for ${typeof from.bpm !== "number" ? from.title : to.title}; BPM score neutral.`);
    }

    // Key compatibility
    const keyC = camelotCompatibility(from.key, to.key);
    if (keyC === null) {
      warnings.push(`Missing/unreadable key for ${from.title} → ${to.title}; key score reduced.`);
    } else {
      const impact = keyC * options.keyWeight;
      score += impact;
      if (keyC >= 0.7) reasons.push({ code: "key_compatible", message: `Keys ${from.key}→${to.key} are harmonically compatible.`, scoreImpact: impact });
    }

    // Energy progression (goal-dependent)
    const ef = this.energyFit(from.energyScore, to.energyScore, options.goal);
    if (ef !== null) {
      const impact = ef * options.energyWeight;
      score += impact;
      if (ef > 0.5) reasons.push({ code: "energy_progression", message: `Energy ${this.fmt(from.energyScore)}→${this.fmt(to.energyScore)} fits ${options.goal}.`, scoreImpact: impact });
    }

    // Semantic + mood continuity
    const semOverlap = this.overlap(from.semanticDomains, to.semanticDomains) + this.overlap(from.semanticCategories, to.semanticCategories);
    if (semOverlap > 0) {
      const impact = clampUnit(semOverlap / 2) * options.semanticWeight;
      score += impact;
      reasons.push({ code: "semantic_continuity", message: `Shares semantic ground with ${from.title}.`, scoreImpact: impact });
    }
    const moodOverlap = this.overlap(from.moodTags, to.moodTags);
    if (moodOverlap > 0) {
      const impact = clampUnit(moodOverlap) * (options.semanticWeight * 0.5);
      score += impact;
      reasons.push({ code: "mood_continuity", message: `Mood continuity with ${from.title}.`, scoreImpact: impact });
    }

    return { score, reasons, warnings };
  }

  /**
   * Greedily order a set of tracks toward the given goal, then apply manual locks.
   * @param tracks - The tracks to order.
   * @param options - Goal, weights, and manual locks controlling the ordering.
   * @returns The ordered result with per-item scores/reasons and overall warnings.
   */
  sortTracks(tracks: DJSEMTrackSortInput[], options: DJSEMTrackSortOptions): DJSEMTrackSortResult {
    const warnings: string[] = [];
    if (tracks.length === 0) return { goal: options.goal, items: [], overallScore: 0, warnings: ["No tracks to sort."] };

    // Deterministic seed: warmup→lowest energy, recovery→highest energy, else first by id.
    const remaining = [...tracks].sort((a, b) => a.preparationTrackId.localeCompare(b.preparationTrackId));
    const seed = this.pickSeed(remaining, options.goal);

    const ordered: DJSEMTrackSortInput[] = [];
    const items: DJSEMTrackSortResultItem[] = [];
    let prev: DJSEMTrackSortInput | null = null;
    let pool = remaining;

    // First element is the seed.
    let current: DJSEMTrackSortInput | undefined = seed;
    while (current) {
      const ts = this.scoreTransition(prev, current, options);
      ordered.push(current);
      items.push({ preparationTrackId: current.preparationTrackId, position: ordered.length - 1, score: round(ts.score), reasons: ts.reasons, warnings: ts.warnings });
      warnings.push(...ts.warnings);
      pool = pool.filter((t) => t.preparationTrackId !== current!.preparationTrackId);
      prev = current;
      // Greedy: highest-scoring next; ties broken deterministically by id.
      current = undefined;
      let best = -Infinity;
      for (const cand of pool) {
        const s = this.scoreTransition(prev, cand, options).score;
        if (s > best || (s === best && current && cand.preparationTrackId.localeCompare(current.preparationTrackId) < 0)) {
          best = s; current = cand;
        }
      }
    }

    // Manual lock pass (positions), preserving as much as possible; conflicts warned.
    const lockResult = options.respectManualLocks
      ? this.applyManualLocks(items, options.manualLocks)
      : { items, warnings: [] as string[] };
    warnings.push(...lockResult.warnings);
    lockResult.items.forEach((it, i) => (it.position = i));

    const overallScore = round(lockResult.items.reduce((s, it) => s + it.score, 0) / Math.max(1, lockResult.items.length));
    return { goal: options.goal, items: lockResult.items, overallScore, warnings: dedupe(warnings) };
  }

  // ---- private --------------------------------------------------------------

  private pickSeed(sorted: DJSEMTrackSortInput[], goal: DJSEMSetSortGoal): DJSEMTrackSortInput {
    const e = (t: DJSEMTrackSortInput) => t.energyScore ?? 0.5;
    if (goal === "warmup_to_peak") return [...sorted].sort((a, b) => e(a) - e(b) || a.preparationTrackId.localeCompare(b.preparationTrackId))[0];
    if (goal === "peak_to_recovery" || goal === "constant_pressure") return [...sorted].sort((a, b) => e(b) - e(a) || a.preparationTrackId.localeCompare(b.preparationTrackId))[0];
    return sorted[0]; // deterministic
  }

  private energyFit(from?: number, to?: number, goal?: DJSEMSetSortGoal): number | null {
    if (typeof from !== "number" || typeof to !== "number") return null;
    const d = to - from;
    switch (goal) {
      case "warmup_to_peak": return clampUnit(0.5 + d); // rising better
      case "peak_to_recovery": return clampUnit(0.5 - d); // falling better
      case "constant_pressure": return clampUnit(1 - Math.abs(d) * 2); // stable high
      case "wave_energy": return clampUnit(0.5 + Math.abs(d)); // movement either way
      case "semantic_story": return 0.3; // energy de-emphasised
      case "manual_assist": default: return 0.5;
    }
  }

  private applyManualLocks(items: DJSEMTrackSortResultItem[], locks: DJSEMManualTrackLock[]): { items: DJSEMTrackSortResultItem[]; warnings: string[] } {
    if (!locks || locks.length === 0) return { items, warnings: [] };
    const warnings: string[] = [];
    const arr = [...items];
    const idxOf = (id: string) => arr.findIndex((x) => x.preparationTrackId === id);

    // positional locks first (lowest position first for stability)
    const positional = locks.filter((l) => typeof l.lockedPosition === "number").sort((a, b) => (a.lockedPosition! - b.lockedPosition!));
    const seenPos = new Map<number, string>();
    for (const l of positional) {
      const from = idxOf(l.preparationTrackId);
      if (from < 0) { warnings.push(`Manual lock references unknown track ${l.preparationTrackId}.`); continue; }
      const pos = Math.min(Math.max(0, l.lockedPosition!), arr.length - 1);
      if (seenPos.has(pos) && seenPos.get(pos) !== l.preparationTrackId) {
        warnings.push(`Conflicting manual locks for position ${pos}; preserving first, shifting ${l.preparationTrackId}.`);
      }
      seenPos.set(pos, l.preparationTrackId);
      const [moved] = arr.splice(from, 1);
      moved.reasons.push({ code: "manual_lock", message: `Locked to position ${pos}.`, scoreImpact: 0 });
      arr.splice(pos, 0, moved);
    }

    // before/after locks (best-effort, warn on impossible combos)
    for (const l of locks.filter((x) => x.lockedBeforeTrackId || x.lockedAfterTrackId)) {
      const me = idxOf(l.preparationTrackId);
      if (me < 0) { warnings.push(`Manual lock references unknown track ${l.preparationTrackId}.`); continue; }
      if (l.lockedBeforeTrackId && l.lockedAfterTrackId) {
        warnings.push(`Track ${l.preparationTrackId} has both before and after locks; applying best-effort.`);
      }
      if (l.lockedAfterTrackId) {
        const anchor = idxOf(l.lockedAfterTrackId);
        if (anchor >= 0 && me <= anchor) { const [m] = arr.splice(me, 1); arr.splice(anchor, 0, m); m.reasons.push({ code: "manual_lock", message: `Locked after ${l.lockedAfterTrackId}.`, scoreImpact: 0 }); }
      }
      if (l.lockedBeforeTrackId) {
        const anchor = idxOf(l.lockedBeforeTrackId);
        const meNow = idxOf(l.preparationTrackId);
        if (anchor >= 0 && meNow > anchor) { const [m] = arr.splice(meNow, 1); arr.splice(anchor, 0, m); m.reasons.push({ code: "manual_lock", message: `Locked before ${l.lockedBeforeTrackId}.`, scoreImpact: 0 }); }
      }
    }
    return { items: arr, warnings };
  }

  private overlap(a: string[], b: string[]): number {
    if (!a?.length || !b?.length) return 0;
    const setB = new Set(b);
    return a.filter((x) => setB.has(x)).length;
  }
  private fmt(n?: number): string { return typeof n === "number" ? n.toFixed(2) : "?"; }
}

function round(n: number): number { return Math.round(n * 1000) / 1000; }
function dedupe(xs: string[]): string[] { return [...new Set(xs)]; }
